// File: src/components/home/PlatformFaq.js
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const PlatformFaq = ({ isDarkMode }) => {
  // Track open state for the main sections (Listeners / Artists)
  const [openSectionKey, setOpenSectionKey] = useState(null);
  // Track open state for the nested questions inside the active section
  const [openQuestionIdx, setOpenQuestionIdx] = useState(null);

  const toggleSection = (sectionKey) => {
    setOpenSectionKey(openSectionKey === sectionKey ? null : sectionKey);
    setOpenQuestionIdx(null); // Reset nested tracking when toggling main categories
  };

  const toggleQuestion = (idx) => {
    setOpenQuestionIdx(openQuestionIdx === idx ? null : idx);
  };

  // Structured Categorized Dataset
  const faqDataset = [
    {
      id: "general",
      title: "General",
      questions: [
        {
          q: "How do you calculate The Top Twenty?",
          a: `The Atomic Top Twenty is calculated by an automated, transparent point system that refreshes every week. To keep the charts fair, dynamic, and community-driven, points are calculated using four simple factors:

            1) Fan Support (+): Listeners move the needle directly. Rating a track "hot" adds 15 points, "not" removes 15 points, and weekly streams add 1 point per stream.

            2) Artist Interactions (+): New releases automatically get Bonus Momentum for their first 5 weeks in the Top 50 to help them compete with older tracks. Engagement with the platforms publishing tools drive additional chart momentum.

            3) Chart Gravity (-): To keep the charts fresh and prevent stagnation, tracks decay over time. Once a track decays to nil it cannot return to the charts.

            4) The Anti-Monopoly Cap: To ensure equal opportunity, we enforce a one song per artist rule inside the official charts. If you have multiple viral hits, your highest-scoring track takes a chart slot.`
        },
      ]
    },
    {
      id: "artists",
      title: "For Independent Artists",
      questions: [
        {
          q: "Why do you block cover songs and unauthorized sampling?",
          "a": "To protect our independent ecosystem and keep our operation compliant with Australian copyright laws, we enforce a strict 100% original music rule. Hosting cover songs or unauthorized remixes requires expensive, complex publishing and mechanical licenses. Staying original-only, allows us to avoid these complexities."
        },
        {
            "q": "What is Atomic's policy on copyright infringement and 'three strikes'?",
            "a": "Atomic maintains a strict stance on intellectual property. If a verified copyright holder issues a formal takedown notice regarding a track containing unauthorized samples, loops, or intellectual property, that track will be removed expeditiously. Profiles that accumulate three separate, verified copyright violations or unauthorized uploads face permanent platform expulsion and account deletion."
        }
      ]
    }
  ];

  return (
    <div className="w-full space-y-4">
      {faqDataset.map((section) => {
        const isSectionOpen = openSectionKey === section.id;

        return (
          <div
            key={section.id}
            className={`border-2 rounded-2xl overflow-hidden transition-all duration-300 ${
              isSectionOpen
                ? (isDarkMode ? "border-brand-pink bg-zinc-900/40" : "border-brand-pink bg-slate-50")
                : (isDarkMode ? "border-zinc-900 bg-zinc-950/20" : "border-slate-200 bg-white")
            }`}
          >
            {/* 🏷️ PRIMARY SECTION TOGGLE HEADLINE */}
            <button
              type="button"
              onClick={() => toggleSection(section.id)}
              className="w-full p-6 flex justify-between items-center text-left focus:outline-none"
            >
              <span className={`text-sm font-black uppercase tracking-wider ${
                isSectionOpen ? "text-brand-pink" : (isDarkMode ? "text-white" : "text-slate-900")
              }`}>
                {section.title}
              </span>
              <span className={`text-base font-black transition-transform duration-300 ${
                isSectionOpen ? "rotate-180 text-brand-pink" : "text-zinc-500"
              }`}>
                &#9662; {/* Minimal downward arrowhead */}
              </span>
            </button>

            {/* 🧱 NESTED SUB-QUESTION ANCHOR LAYOUT */}
            <AnimatePresence initial={false}>
              {isSectionOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                >
                  <div className={`p-6 pt-0 space-y-3 border-t border-dotted ${
                    isDarkMode ? "border-zinc-800" : "border-slate-200"
                  }`}>
                    {section.questions.map((item, idx) => {
                      const isQuestionOpen = openQuestionIdx === idx;

                      return (
                        <div
                          key={idx}
                          className={`border rounded-xl overflow-hidden transition-colors duration-200 ${
                            isDarkMode ? "border-zinc-800/80 bg-black/20" : "border-slate-200 bg-white"
                          }`}
                        >
                          {/* ❓ Sub-Question Row Trigger */}
                          <button
                            type="button"
                            onClick={() => toggleQuestion(idx)}
                            className="w-full p-4 flex justify-between items-center text-left focus:outline-none"
                          >
                            <span className={`text-xs font-bold tracking-tight pr-4 ${
                              isDarkMode ? "text-zinc-300" : "text-slate-700"
                            }`}>
                              {item.q}
                            </span>
                            <span className={`text-xs font-bold transform transition-transform duration-200 ${
                              isQuestionOpen ? 'rotate-45 text-brand-pink' : 'text-zinc-500'
                            }`}>
                              ＋
                            </span>
                          </button>

                          {/* 📖 Text Answer Container Block */}
                          <AnimatePresence initial={false}>
                            {isQuestionOpen && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                <div className={`p-4 pt-0 text-xs font-medium leading-relaxed border-t whitespace-pre-line ${
                                    isDarkMode 
                                        ? "text-zinc-400 border-zinc-800/40 bg-zinc-950/40" 
                                        : "text-slate-600 border-slate-100 bg-slate-50/50"
                                    }`}>
                                    {item.a}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

export default PlatformFaq;